import { isAbort, pb } from './pb'
import { readRoles } from './session'
import type { CovellaUser } from './session'

/**
 * The Keepers' forum, against the collections in `backend/forum_collections.json`.
 *
 * Reading and posting are both gated on `covella_member` by the collection rules, so a signed-in
 * woman who is not a Keeper gets nothing back. The site checks `isMember` first and says so
 * plainly, but the rules are the boundary: this file only decides what to show.
 *
 * Posting is also gated by `pb_hooks/forum_moderation.pb.js` (mutes, bans, the guidelines, a
 * category's own posting rules). The hook's sentence comes back as the error message and is shown
 * as is, because it says which of those it was.
 */

export interface ForumUser {
  id: string
  name?: string
}

export interface ForumCategory {
  id: string
  name: string
  description: string
  parent: string
  sort_order: number
  post_roles: string[]
  reply_roles: string[]
  read_only: boolean
}

export interface ForumTopic {
  id: string
  category: string
  author: string
  title: string
  body: string
  pinned: boolean
  locked: boolean
  created: string
  updated: string
  expand?: { author?: ForumUser; category?: ForumCategory }
}

export interface ForumReply {
  id: string
  topic: string
  author: string
  body: string
  created: string
  expand?: { author?: ForumUser }
}

/** Must match `GUIDELINES_VERSION` in forum_moderation.pb.js and the app's FORUM_GUIDELINES_VERSION. */
export const FORUM_GUIDELINES_VERSION = 1

const STAFF_ROLES = ['admin', 'elders_wife', 'moderator']

export function isStaff(user: Pick<CovellaUser, 'role'>): boolean {
  const held = readRoles(user).map((role) => role.trim().toLowerCase())
  return STAFF_ROLES.some((role) => held.includes(role))
}

/** Empty role lists mean everyone, as the hook reads them. Staff bypass all of it. */
export function canStartTopic(category: ForumCategory, user: CovellaUser): boolean {
  if (isStaff(user)) return true
  return !category.read_only && category.post_roles.length === 0
}
export function canReply(category: ForumCategory | undefined, user: CovellaUser): boolean {
  if (isStaff(user)) return true
  if (!category) return true
  return !category.read_only && category.reply_roles.length === 0
}

export function authorOf(entry: { expand?: { author?: ForumUser } }): string {
  return entry.expand?.author?.name?.trim() || 'Sister'
}

export async function loadCategories(): Promise<ForumCategory[]> {
  const result = await pb.collection('forum_categories').getList<ForumCategory>(1, 200, {
    sort: 'sort_order,name',
    requestKey: null,
  })
  return result.items
}

export async function loadTopics(options: { category?: string; limit?: number } = {}): Promise<ForumTopic[]> {
  const result = await pb.collection('forum_topics').getList<ForumTopic>(1, options.limit ?? 200, {
    sort: '-pinned,-created',
    expand: 'author,category',
    filter: options.category ? pb.filter('category = {:c}', { c: options.category }) : '',
    requestKey: null,
  })
  return result.items
}

export async function loadTopic(id: string): Promise<ForumTopic | null> {
  try {
    return await pb.collection('forum_topics').getOne<ForumTopic>(id, {
      expand: 'author,category',
      requestKey: null,
    })
  } catch (err) {
    if (isAbort(err)) return null
    throw err
  }
}

export async function loadReplies(topicId: string): Promise<ForumReply[]> {
  const result = await pb.collection('forum_replies').getList<ForumReply>(1, 500, {
    sort: 'created',
    expand: 'author',
    filter: pb.filter('topic = {:t}', { t: topicId }),
    requestKey: null,
  })
  return result.items
}

export interface TopicStats {
  replies: number
  lastAt: string
  lastBy: string
}

export interface ForumStats {
  /** Replies and the latest post per topic. A topic with no replies is last posted by its author. */
  byTopic: Record<string, TopicStats>
  /** Topics plus replies written by each author, for the little count beside a post. */
  postsBy: Record<string, number>
}

/** One light fetch of every reply, folded here rather than a count per row on screen. */
export async function loadStats(topics: ForumTopic[]): Promise<ForumStats> {
  const byTopic: Record<string, TopicStats> = {}
  const postsBy: Record<string, number> = {}
  for (const topic of topics) {
    byTopic[topic.id] = { replies: 0, lastAt: topic.created, lastBy: authorOf(topic) }
    postsBy[topic.author] = (postsBy[topic.author] ?? 0) + 1
  }
  try {
    const rows = await pb.collection('forum_replies').getFullList<{
      topic: string
      author: string
      created: string
      expand?: { author?: ForumUser }
    }>({
      fields: 'topic,author,created,expand.author.name',
      expand: 'author',
      sort: 'created',
      requestKey: null,
    })
    for (const row of rows) {
      const stats = byTopic[row.topic]
      if (stats) {
        stats.replies += 1
        stats.lastAt = row.created
        stats.lastBy = authorOf(row)
      }
      postsBy[row.author] = (postsBy[row.author] ?? 0) + 1
    }
  } catch (err) {
    if (!isAbort(err)) throw err
  }
  return { byTopic, postsBy }
}

export async function createTopic(
  userId: string,
  category: string,
  title: string,
  body: string,
): Promise<ForumTopic> {
  return pb.collection('forum_topics').create<ForumTopic>({
    category,
    author: userId,
    title,
    body,
    pinned: false,
    locked: false,
  })
}

export async function createReply(userId: string, topic: string, body: string): Promise<void> {
  await pb.collection('forum_replies').create({ topic, author: userId, body })
}

export async function hasAcceptedGuidelines(userId: string): Promise<boolean> {
  try {
    const result = await pb.collection('forum_guideline_acceptances').getList(1, 1, {
      filter: pb.filter('user = {:u} && version >= {:v}', { u: userId, v: FORUM_GUIDELINES_VERSION }),
      requestKey: null,
    })
    return result.items.length > 0
  } catch {
    return false
  }
}

export async function acceptGuidelines(userId: string): Promise<void> {
  await pb.collection('forum_guideline_acceptances').create({
    user: userId,
    version: FORUM_GUIDELINES_VERSION,
  })
}
